import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import DatePicker from "../../components/DatePicker";
import moment from "moment";
import "moment/locale/th";

/*  helper: format comma */
function formatWithCommas(numStr) {
  if (numStr === null || numStr === undefined || numStr === "") return "";
  const parts = numStr.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

/*  helper: ป้องกัน พ.ศ. เพี้ยน -> ค.ศ. */
const toAD = (dateStr) => {
  if (!dateStr) return "";
  let m = moment(dateStr, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601]);
  if (m.isValid() && m.year() > 2400) {
    m = m.subtract(543, "years");
  }
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

/*  NumberInput: padding/height เท่า DatePicker */
const NumberInput = ({ value, onValueChange, disabled = false }) => {
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      if (value === null || value === undefined || value === "") {
        setLocalValue("");
      } else {
        setLocalValue(formatWithCommas(parseFloat(value).toFixed(2)));
      }
    }
  }, [value]);

  const handleFocus = (e) => e.target.select();
  const handleChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(raw)) return;
    const parts = raw.split(".");
    if (parts[1]?.length > 2) return;
    let displayVal = raw;
    if (parts[0].length > 3) displayVal = formatWithCommas(raw);
    setLocalValue(displayVal);
    const numericValue = parseFloat(raw) || 0;
    onValueChange(numericValue);
  };

  const handleBlur = () => {
    const numericValue = parseFloat(localValue.replace(/,/g, "")) || 0;
    const formatted = formatWithCommas(numericValue.toFixed(2));
    setLocalValue(formatted);
    onValueChange(numericValue);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={localValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={disabled}
      className="w-full h-[42px] border border-gray-300 rounded-md px-3 py-2 text-right text-[14px] focus:ring-1 focus:ring-[#E13F57] focus:border-[#E13F57] transition bg-white"
      inputMode="decimal"
    />
  );
};

export default function ContractPaymentTable({
  payments,
  setPayments,
  grandTotal = 0,
  onValidationChange,
  isReadOnly = false,
  startDate = null,
  endDate = null,
}) {
  const [installments, setInstallments] = useState(1);
  const [perMonth, setPerMonth] = useState(1); // เดือนละกี่งวด
  const [payDay, setPayDay] = useState(1); // วันที่ชำระงวด (กำหนดเอง)
  const [startMonth, setStartMonth] = useState("01");
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [totalAmount, setTotalAmount] = useState(0);

  const months = [
    { value: "01", label: "มกราคม" },
    { value: "02", label: "กุมภาพันธ์" },
    { value: "03", label: "มีนาคม" },
    { value: "04", label: "เมษายน" },
    { value: "05", label: "พฤษภาคม" },
    { value: "06", label: "มิถุนายน" },
    { value: "07", label: "กรกฎาคม" },
    { value: "08", label: "สิงหาคม" },
    { value: "09", label: "กันยายน" },
    { value: "10", label: "ตุลาคม" },
    { value: "11", label: "พฤศจิกายน" },
    { value: "12", label: "ธันวาคม" },
  ];

  // ไม่สร้างการชำระเงินอัตโนมัติแล้ว - ให้ผู้ใช้กดปุ่ม "กำหนดค่าเริ่มต้น" เอง

  useEffect(() => {
    if (!payments || !Array.isArray(payments) || payments.length === 0) return;

    // ห้าม normalize ซ้ำ
    if (payments.__normalized) return;
    if (payments.__autoGenerated) return; // ข้ามถ้าเป็น auto-generated

    // ตรวจสอบว่าข้อมูลยังไม่ถูก normalize แล้ว (ยังมี CT_Due_Amount หรือ CT_Due_Date)
    const needsNormalization = payments.some(
      (p) => p.CT_Due_Amount !== undefined || p.CT_Due_Date !== undefined
    );

    if (!needsNormalization) return; // ถ้าไม่ต้อง normalize ให้ข้าม

    const normalized = payments
      .filter((p) => p && typeof p === 'object') // กรองเฉพาะ object ที่ถูกต้อง
      .map((p, index) => {
        // normalize วันที่จาก API ให้เป็น ค.ศ.
        const apiDate = p.CT_Due_Date || p.date;
        let dateForState = "";
        if (apiDate) {
          // ตรวจสอบว่าข้อมูลเป็น ค.ศ. หรือ พ.ศ.
          const m = moment(apiDate, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601]);
          if (m.isValid()) {
            if (m.year() > 2400) {
              // เป็น พ.ศ. อยู่แล้ว ให้แปลงเป็น ค.ศ. ก่อน แล้วเก็บเป็น ค.ศ.
              dateForState = m.subtract(543, "years").format("YYYY-MM-DD");
            } else {
              // เป็น ค.ศ. เก็บเป็น ค.ศ. (ไม่ต้องแปลง)
              dateForState = m.format("YYYY-MM-DD");
            }
          }
        }
        return {
          installment: p.Line || p.installment || index + 1,
          date: dateForState || toAD(p.date), // ใช้ dateForState ถ้ามี หรือใช้ toAD สำหรับกรณีอื่น
          amount: Number(p.CT_Due_Amount || p.amount || 0),
          // invoice_no จะถูก generate ที่ backend อัตโนมัติ
        };
      });

    // ตรวจสอบว่า normalized ไม่ว่างเปล่า
    if (normalized.length === 0) return;

    normalized.__normalized = true;

    setPayments(normalized);
    setInstallments(normalized.length);

    if (normalized.length > 0) {
      const first = moment(normalized[0].date, "YYYY-MM-DD");
      const second = normalized[1]
        ? moment(normalized[1].date, "YYYY-MM-DD")
        : null;

      if (first.isValid()) {
        setPayDay(first.date());
        setStartMonth(first.format("MM"));
        setStartYear(first.year());
      }

      // คำนวณ perMonth จากวันที่จริงของงวดแรกและงวดที่สอง
      // ใช้วิธีคำนวณที่แม่นยำกว่า โดยดูจากวันที่จริง
      if (second && second.isValid() && first.isValid()) {
        // คำนวณความแตกต่างเป็นเดือน (ใช้ true เพื่อให้ได้ทศนิยม)
        const diffMonths = second.diff(first, "months", true);
        
        // ถ้าความแตกต่างน้อยกว่า 0.5 เดือน ให้ถือว่าเป็น 1 เดือน
        // ถ้ามากกว่า 0.5 เดือน ให้ปัดเศษเป็นจำนวนเต็มที่ใกล้ที่สุด
        let calculatedPerMonth = 1;
        if (diffMonths >= 0.5) {
          calculatedPerMonth = Math.round(diffMonths);
          // ตรวจสอบว่าค่าที่คำนวณได้สมเหตุสมผลหรือไม่ (1-12 เดือน)
          if (calculatedPerMonth < 1) calculatedPerMonth = 1;
          if (calculatedPerMonth > 12) calculatedPerMonth = 12;
        }
        
        setPerMonth(calculatedPerMonth);
      } else {
        setPerMonth(1);
      }
      
      // ตั้งค่า installments ให้ตรงกับจำนวนงวดจริงใน database
      setInstallments(normalized.length);
    }
  }, [payments]);




  /*  คำนวณยอดรวม */
  useEffect(() => {
    // กรองเฉพาะข้อมูลที่ถูกต้อง (ไม่รวม property ที่เป็น metadata)
    const validPayments = Array.isArray(payments) 
      ? payments.filter((p) => p && typeof p === 'object' && 'amount' in p)
      : [];
    
    const sum = validPayments.reduce(
      (acc, cur) => {
        const amount = Number(cur.amount) || 0;
        return acc + amount;
      },
      0
    );
    setTotalAmount(sum);
  }, [payments]);

  /*  ตรวจสอบว่าผลรวมตรงกับ grandTotal หรือไม่ */
  useEffect(() => {
    if (!onValidationChange) return;
    
    const grandTotalNum = Number(grandTotal) || 0;
    const totalAmountNum = Number(totalAmount) || 0;
    // ใช้ tolerance 0.01 สำหรับ floating point comparison
    const isValid = Math.abs(grandTotalNum - totalAmountNum) < 0.01;
    
    onValidationChange(isValid);
  }, [totalAmount, grandTotal, onValidationChange]);

  /*  generate ค่าแบบใหม่ */
  const generatePayments = () => {
    const total = Number(grandTotal) || 0;
    if (total <= 0 || installments <= 0) return;

    const newPayments = [];
    let startDate = moment(`${startYear}-${startMonth}-${payDay}`, "YYYY-MM-DD");

    // คำนวณราคาต่องวดโดยปัดลง (เพื่อให้เศษเหลือไปที่งวดสุดท้าย)
    const perInstallment = Math.floor((total / installments) * 100) / 100; // ปัดลง 2 ตำแหน่ง

    for (let i = 0; i < installments; i++) {
      let paymentDate = moment(startDate).add(i * perMonth, "months");
      const adDate = paymentDate.format("YYYY-MM-DD");

      // งวดสุดท้ายให้รวมเศษเหลือทั้งหมดเพื่อให้ผลรวมตรงกับ total พอดี
      const finalAmount =
        i === installments - 1
          ? Number((total - perInstallment * (installments - 1)).toFixed(2))
          : perInstallment;

      newPayments.push({
        installment: i + 1,
        date: adDate,
        amount: finalAmount,
        // invoice_no จะถูก generate ที่ backend อัตโนมัติ
      });
    }

    setPayments(newPayments);
  };

  const handleChange = (idx, field, value) => {
    const newPayments = [...payments];
    newPayments[idx][field] = value;
    setPayments(newPayments);
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6 border border-gray-300 text-[14px]">
      <h3 className="font-bold text-gray-700 text-lg mb-4 flex items-center gap-2">
        <Icon
          icon="mdi:credit-card-clock-outline"
          className="w-5 h-5 text-[#E13F57]"
        />
        การชำระเงิน
      </h3>

      {/*  Options */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span>แบ่งจ่าย</span>
        <input
          type="number"
          value={installments}
          min={1}
          onChange={(e) => setInstallments(parseInt(e.target.value) || 1)}
          disabled={isReadOnly}
          className="w-20 h-[42px] border border-gray-300 rounded-md px-3 text-center focus:ring-1 focus:ring-[#E13F57]"
        />

        <span>งวด</span>
        <input
          type="number"
          value={perMonth}
          min={1}
          onChange={(e) => setPerMonth(parseInt(e.target.value) || 1)}
          disabled={isReadOnly}
          className="w-20 h-[42px] border border-gray-300 rounded-md px-3 text-center focus:ring-1 focus:ring-[#E13F57]"
        />
        <span>เดือนต่อครั้ง</span>
        <span>วันที่จ่าย</span>
        <input
          type="number"
          value={payDay}
          min={1}
          max={28}
          onChange={(e) => setPayDay(parseInt(e.target.value) || 1)}
          disabled={isReadOnly}
          className="w-20 h-[42px] border border-gray-300 rounded-md px-3 text-center focus:ring-1 focus:ring-[#E13F57]"
        />

        <span>เริ่มจาก</span>
        <select
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          disabled={isReadOnly}
          className="h-[42px] border border-gray-300 rounded-md px-3 py-2 focus:ring-1 focus:ring-[#E13F57]"
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>

        <select
          value={startYear}
          onChange={(e) => setStartYear(e.target.value)}
          disabled={isReadOnly}
          className="h-[42px] border border-gray-300 rounded-md px-3 py-2 focus:ring-1 focus:ring-[#E13F57]"
        >
          {Array.from({ length: 5 }, (_, i) => {
            const y = new Date().getFullYear() + i;
            return (
              <option key={y} value={y}>
                {y + 543}
              </option>
            );
          })}
        </select>

        {!isReadOnly && (
          <button
            onClick={generatePayments}
            className="flex items-center gap-1 bg-[#E13F57] hover:bg-[#c53048] text-white px-4 h-[42px] rounded-md shadow transition"
          >
            <Icon icon="mdi:refresh" className="w-5 h-5" />
            กำหนดค่าเริ่มต้น
          </button>
        )}
      </div>

      {/*  Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-300">
        <table className="table-fixed w-full border border-gray-300 text-[14px]">
          <thead>
            <tr>
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">
                งวดที่
              </th>
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[40%] py-2">
                วันที่ (พ.ศ.)
              </th>
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[40%] py-2">
                จำนวนเงิน
              </th>
            </tr>
          </thead>

          <tbody>
            {payments.length > 0 ? (
              payments
                .filter((p) => p && typeof p === 'object' && 'amount' in p && 'date' in p)
                .map((p, idx) => (
                <tr
                  key={idx}
                  className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition"
                >
                  <td className="text-center border border-gray-300 py-2 align-middle">
                    {p.installment}
                  </td>

                  <td className="text-center border border-gray-300 px-3 py-1 align-middle">
                    <DatePicker
                      name={`date-${idx}`}
                      value={p.date || ""}
                      disabled={isReadOnly}
                      onChange={(date) => {
                        if (!isReadOnly) {
                          handleChange(idx, "date", date || "");
                        }
                      }}

                      withPortal
                      portalId="contract-payment-datepicker-portal"
                      popperPlacement="top-end"
                      className="w-full h-[42px] border border-gray-300 rounded-md px-3 py-2 text-center 
                        focus:ring-1 focus:ring-[#E13F57] focus:border-[#E13F57] transition bg-white"
                    />
                  </td>

                  <td className="text-right border border-gray-300 px-3 py-1 align-middle">
                    <NumberInput
                      value={p.amount}
                      disabled={isReadOnly}
                      onValueChange={(val) => {
                        if (!isReadOnly) {
                          handleChange(idx, "amount", val);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="3"
                  className="text-center py-4 text-gray-400 border border-gray-300"
                >
                  ยังไม่มีตารางการชำระเงิน
                </td>
              </tr>
            )}
          </tbody>

          {payments.length > 0 && (
            <tfoot>
              <tr className="bg-gray-100 font-semibold border-t border-gray-300 h-[44px]">
                <td colSpan="2" className="text-right pr-3 align-middle">
                  รวมทั้งหมด
                </td>
                <td className="text-right pr-3 text-gray-800 align-middle font-bold text-[15px]">
                  {formatWithCommas(totalAmount.toFixed(2))} บาท
                </td>
              </tr>
              {(() => {
                const grandTotalNum = Number(grandTotal) || 0;
                const totalAmountNum = Number(totalAmount) || 0;
                const diff = Math.abs(grandTotalNum - totalAmountNum);
                const isValid = diff < 0.01;
                
                if (!isValid && payments.length > 0) {
                  return (
                    <tr className="bg-red-50 border-t border-red-200">
                      <td colSpan="3" className="text-center py-2 align-middle">
                        <span className="text-red-600 font-semibold text-[14px] flex items-center justify-center gap-2">
                          <Icon icon="mdi:alert-circle" className="w-5 h-5" />
                          ผลรวมการชำระเงิน ({formatWithCommas(totalAmountNum.toFixed(2))}) 
                          ไม่ตรงกับยอดรวมท้ายบิล ({formatWithCommas(grandTotalNum.toFixed(2))})
                          {diff >= 0.01 && (
                            <span className="text-red-700">
                              (ต่างกัน {formatWithCommas(diff.toFixed(2))} บาท)
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                }
                return null;
              })()}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
